import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDemandViewDto,
  CreateDemandViewResponseDto,
  GetAllDemandViewsDto,
  GetDemandViewByIdResponseDto,
  GetDemandViewsResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { demandViewsQueryKeys } from '@/lib/query-keys/demandViews';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useDemandViewsApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/demand-views`;
  const defaultLimit = 25;

  const getDemandViews = async (
    params: GetAllDemandViewsDto,
  ): Promise<GetDemandViewsResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}`, { params });
    return data;
  };

  const getDemandView = async (id: number): Promise<GetDemandViewByIdResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`);
    return data;
  };

  const createDemandView = async (
    view: CreateDemandViewDto,
  ): Promise<CreateDemandViewResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}`, view);
    return data;
  };

  const deleteDemandView = async (id: number) => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return { data };
  };

  const useGetAll = (params: GetAllDemandViewsDto = {}) => {
    const limit = params.limit ?? defaultLimit;
    const normalizedParams = { ...params, limit };
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: demandViewsQueryKeys.list(normalizedParams),
      queryFn: ({ pageParam }) => getDemandViews({ ...params, limit, offset: pageParam * limit }),
      getNextPageParam: (lastPage) =>
        lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined,
    });
  };

  const useGetDemandView = (id: number) =>
    useQuery({
      queryKey: demandViewsQueryKeys.detail(id),
      queryFn: () => getDemandView(id),
      enabled: id > 0,
    });

  const queryClient = useQueryClient();

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandView,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: demandViewsQueryKeys.all() }),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemandView,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: demandViewsQueryKeys.all() }),
    });

  return {
    getDemandViews,
    useGetAll,
    useGetDemandView,
    useCreate,
    useDelete,
  };
};
